import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import MarkdownView from "@/components/MarkdownView";
import { ArrowLeft, CalendarDays, ArrowRight } from "lucide-react";

const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    api.get(`/blog/public/${slug}`)
      .then((r) => { setPost(r.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const origin = window.location.origin;
    const url = `${origin}/blog/${post.slug}`;
    const title = post.seo_title || `${post.title} — SphereHyx Blog`;
    const desc = post.seo_description || post.excerpt || "";
    const cover = post.cover_image ? (post.cover_image.startsWith("http") ? post.cover_image : origin + post.cover_image) : `${origin}/og-image.png`;
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", cover, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", cover);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", url);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "blog-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: post.title, description: desc, image: cover,
      datePublished: post.published_at, dateModified: post.updated_at,
      author: { "@type": "Person", name: post.author_name || "SphereHyx" },
      publisher: { "@type": "Organization", name: "SphereHyx", logo: { "@type": "ImageObject", url: `${origin}/logo512.png` } },
      mainEntityOfPage: url,
    });
    document.getElementById("blog-jsonld")?.remove();
    document.head.appendChild(ld);
    return () => {
      document.getElementById("blog-jsonld")?.remove();
      document.title = "SphereHyx — Connect · Manage · Grow";
    };
  }, [post]);

  if (status === "loading") return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground" data-testid="post-loading">Loading…</div>;
  if (status === "notfound") return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="post-notfound">
      <h1 className="font-heading font-bold text-2xl">Post not found</h1>
      <p className="text-muted-foreground">This article may have been moved or unpublished.</p>
      <Button asChild className="brand-gradient text-white border-0 rounded-full"><Link to="/blog">Back to blog</Link></Button>
    </div>
  );

  const origin = window.location.origin;
  const cover = post.cover_image ? (post.cover_image.startsWith("http") ? post.cover_image : origin + post.cover_image) : "";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="blog-post-page">
      <header className="flex items-center justify-between px-6 py-5 max-w-3xl mx-auto">
        <Link to="/" data-testid="post-logo-link"><Logo /></Link>
        <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground"><Link to="/blog" data-testid="post-back-link"><ArrowLeft size={16} className="mr-1.5" /> All posts</Link></Button>
      </header>

      <article className="max-w-3xl mx-auto px-6 pb-16">
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="post-tags">
            {post.tags.map((t) => <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="text-[11px] uppercase tracking-wider text-secondary font-semibold">{t}</Link>)}
          </div>
        )}
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="post-title">{post.title}</h1>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-5"><CalendarDays size={14} /> {fmtDate(post.published_at)} · {post.author_name}</div>
        {cover && <img src={cover} alt={post.title} className="w-full rounded-2xl border border-border mt-7 aspect-[16/9] object-cover" data-testid="post-cover" />}
        <div className="mt-8">
          <MarkdownView>{post.body}</MarkdownView>
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-card p-6 text-center">
          <h3 className="font-heading font-bold text-xl">Run your club with SphereHyx</h3>
          <p className="text-sm text-muted-foreground mt-1.5">Teams, attendance, payments and tournaments — all in one place. Free to start.</p>
          <Button asChild className="brand-gradient text-white border-0 rounded-full mt-4 px-6"><Link to="/register" data-testid="post-cta-btn">Create your club free <ArrowRight size={16} className="ml-1.5" /></Link></Button>
        </div>
      </article>

      <footer className="border-t border-border py-10 px-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <Logo />
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-foreground transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
